/**
 * OutletLocatorPage — find a NIPOST outlet near you.
 *
 * Wired to the public `GET /v1/outlets` endpoint on service-customer
 * (OutletController, marked `@Public()`). The earlier "static directory +
 * deferral banner" implementation has been replaced now that the backend
 * ships.
 *
 * UX:
 *   - Search input (debounced 300ms) → re-fetches with `?q=…`.
 *   - Loading / error / empty / results states.
 *   - Each result card shows name, full address, services badges, opening
 *     hours snippet, and a "Get directions" link that opens Google Maps with
 *     lat/lng (falling back to the postal address when coordinates are
 *     missing).
 *   - Clicking a card expands it inline to show full opening hours and
 *     contact details (the e2e spec asserts on this).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { ArrowRight, Building2, MapPin, Navigation, Phone, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PageHeader } from './PortalUI';
import { listOutlets, type Outlet } from '../../lib/api/outlets';

/** Debounce delay for the search input. 300ms matches the rest of the
 *  customer portal (cf. CustomerShipments, ParcelBookingPage). */
const SEARCH_DEBOUNCE_MS = 300;

/** Page-size for the outlet listing. Fits comfortably on a desktop screen
 *  without pagination controls (the directory ships ~14 rows; growth past
 *  this will add a Load-more button in a separate slice). */
const PAGE_SIZE = 50;

type FetchState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  | { readonly kind: 'ok'; readonly items: readonly Outlet[]; readonly total: number };

/** Lookup table for the small set of service codes the seed data ships. The
 *  backend will accept any string; unknown codes render as the raw value. */
const SERVICE_LABELS: Readonly<Record<string, string>> = {
  DROPOFF: 'Drop-off',
  PICKUP: 'Pickup',
  PAYMENT: 'Bill payment',
  KYC: 'KYC / NIN',
};

/** Opening-hours short-code → human label, in display order. */
const DAY_ORDER: readonly (readonly [string, string])[] = [
  ['mon', 'Mon'],
  ['tue', 'Tue'],
  ['wed', 'Wed'],
  ['thu', 'Thu'],
  ['fri', 'Fri'],
  ['sat', 'Sat'],
  ['sun', 'Sun'],
];

function formatAddress(o: Outlet): string {
  const parts = [o.addressLine1, o.addressLine2, o.city, o.lga, o.state, o.postalCode]
    .filter((p): p is string => p !== null && p !== undefined && p.trim().length > 0);
  return parts.join(', ');
}

function directionsHref(o: Outlet): string {
  if (o.latitude && o.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${o.latitude},${o.longitude}`)}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formatAddress(o))}`;
}

export function OutletLocatorPage(): JSX.Element {
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Debounce the search term so we don't hammer the backend on every keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  // Fetch on mount + whenever the debounced query changes. Aborts any
  // in-flight request when the input changes again to avoid out-of-order
  // responses overwriting the latest result.
  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState((prev) => (prev.kind === 'ok' ? prev : { kind: 'loading' }));

    listOutlets(
      { q: debouncedQuery.length > 0 ? debouncedQuery : undefined, pageSize: PAGE_SIZE },
      { signal: controller.signal },
    )
      .then((res) => {
        if (controller.signal.aborted) return;
        setState({ kind: 'ok', items: res.items, total: res.total });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const message =
          err instanceof Error ? err.message : 'Could not load outlets. Please try again.';
        setState({ kind: 'error', message });
      });

    return () => controller.abort();
  }, [debouncedQuery]);

  const onPickForDropoff = useCallback((outletId: string): void => {
    try {
      window.sessionStorage.setItem(
        'upmp.customer.preferredOutlet',
        JSON.stringify({ outletId, recordedAt: new Date().toISOString() }),
      );
    } catch {
      // sessionStorage unavailable — silently ignore.
    }
  }, []);

  const items = state.kind === 'ok' ? state.items : [];
  const isLoading = state.kind === 'loading';
  const isError = state.kind === 'error';
  const isEmptyResult = state.kind === 'ok' && items.length === 0;

  const headerSubtitle = useMemo<string>(() => {
    if (state.kind === 'ok') {
      return `${state.total} outlet${state.total === 1 ? '' : 's'} found`;
    }
    return 'Loading outlets…';
  }, [state]);

  return (
    <div>
      <PageHeader
        title="Find a NIPOST Outlet"
        description="Pick an outlet near you for drop-off, pickup, NIN verification, or bill payments."
      />

      <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, city, LGA, state, or outlet code…"
            aria-label="Outlet search"
            data-testid="outlet-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
          />
        </div>
        <p className="text-xs text-muted whitespace-nowrap" data-testid="outlet-result-summary">
          {headerSubtitle}
        </p>
      </div>

      <div className="mt-4 space-y-2" data-testid="outlet-results">
        {isLoading ? (
          <div
            className="bg-white border border-border rounded-xl p-8 text-center text-sm text-muted"
            data-testid="outlet-loading"
          >
            Loading outlets…
          </div>
        ) : isError ? (
          <div
            className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm"
            role="alert"
            data-testid="outlet-error"
          >
            <p className="font-semibold">Could not load outlets.</p>
            <p className="mt-1 text-xs">{state.kind === 'error' ? state.message : ''}</p>
          </div>
        ) : isEmptyResult ? (
          <div
            className="bg-white border border-border rounded-xl p-8 text-center text-sm text-muted"
            data-testid="outlet-empty"
          >
            No outlets match {`"${debouncedQuery || 'your search'}"`}. Try a different city or
            state.
          </div>
        ) : (
          items.map((outlet) => (
            <OutletCard
              key={outlet.id}
              outlet={outlet}
              expanded={expandedId === outlet.id}
              onToggle={() =>
                setExpandedId((prev) => (prev === outlet.id ? null : outlet.id))
              }
              onPickForDropoff={() => onPickForDropoff(outlet.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface OutletCardProps {
  readonly outlet: Outlet;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly onPickForDropoff: () => void;
}

function OutletCard({ outlet, expanded, onToggle, onPickForDropoff }: OutletCardProps): JSX.Element {
  const fullAddress = formatAddress(outlet);
  return (
    <div
      className="bg-white border border-border rounded-xl overflow-hidden"
      data-testid="outlet-card"
      data-outlet-code={outlet.outletCode}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full text-left p-4 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm text-gray-900">{outlet.name}</p>
            <p
              className="mt-0.5 text-xs text-muted inline-flex items-start gap-1"
              data-testid="outlet-address"
            >
              <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{fullAddress}</span>
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {outlet.services.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-700 rounded"
                >
                  <Building2 className="w-2.5 h-2.5" /> {SERVICE_LABELS[s] ?? s}
                </span>
              ))}
            </div>
          </div>
          <span className="text-[10px] font-mono text-muted whitespace-nowrap">
            {outlet.outletCode}
          </span>
        </div>
      </button>

      {expanded ? (
        <div className="px-4 pb-4 border-t border-border bg-gray-50/50" data-testid="outlet-detail">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1">
                Opening hours
              </p>
              <ul className="text-xs text-gray-700 space-y-0.5">
                {DAY_ORDER.map(([code, label]) => {
                  const value = outlet.openingHours[code];
                  return (
                    <li key={code} className="flex justify-between gap-2">
                      <span className="text-muted">{label}</span>
                      <span className="font-mono">{value && value.length > 0 ? value : 'Closed'}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="space-y-2">
              {outlet.phone ? (
                <p className="text-xs text-gray-700 inline-flex items-center gap-1.5">
                  <Phone className="w-3 h-3" /> {outlet.phone}
                </p>
              ) : null}
              {outlet.email ? (
                <p className="text-xs text-gray-700 break-all">{outlet.email}</p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <a
                  href={directionsHref(outlet)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="outlet-directions"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-border text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50"
                >
                  <Navigation className="w-3 h-3" /> Get directions
                </a>
                <Link
                  to="/portal/parcel-booking"
                  onClick={onPickForDropoff}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white text-xs font-bold rounded-lg hover:bg-nipost-green-dark"
                >
                  Pick for drop-off <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
